import IAppEvents from '@app/lib/IAppEvents';
import IAppLogger from '@app/lib/IAppLogger';
import environment from '@lib/environment';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import {
  NO_ACTIVE_CONTRACT,
  TOKEN_REFRESH_BUFFER_SECONDS,
} from './constants/auth';
import {
  AuthEvents,
  AuthCanSignoutHandler,
  AuthSignoutHandler,
  AuthUser,
} from './types';
import { AuthFlow } from './lib/AuthFlow';
import getCurrentServices from './lib/getCurrentServices';
import getCurrentUser from './lib/getCurrentUser';
import { authState } from './state';
import { selectAuthPath, readStickyPath } from './lib/authPath';
import {
  probeBffUser,
  fetchBffToken,
  redirectToBffLogin,
  redirectToBffLogout,
} from './lib/bffSession';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import getWebPhoneToken from './lib/getWebPhoneToken';
import { UserSubscriptionManager } from './lib/UserSubscriptionManager';
import { hasValidSeat } from '@constants/user';

const AUTH_FORCE_LOGIN_HOURS = 10;

export class AuthModule extends AppModule {
  private authFlow: AuthFlow;
  private readonly subscriptionManager: UserSubscriptionManager;
  override events = [
    AuthEvents.SIGNIN,
    AuthEvents.SIGNOUT,
    AuthEvents.USER_UPDATED,
  ];
  private signoutHandlers: Record<string, AuthSignoutHandler> = {};
  private canSignoutHandlers: Record<string, AuthCanSignoutHandler> = {};
  private tokenRefreshPromise: Promise<string> | null = null;
  private bffTokenFetchPromise: Promise<string> | null = null;
  private refreshAuthInterval: NodeJS.Timeout | null = null;
  private sessionExpiredShown = false;

  constructor(events: IAppEvents, logger: IAppLogger) {
    super(events, logger);

    // create the AuthFlow helper class
    const { AUTH_IUM_ISSUER_URL, AUTH_IUM_CLIENT_ID } = environment;
    this.authFlow = new AuthFlow(
      AUTH_IUM_ISSUER_URL,
      AUTH_IUM_CLIENT_ID,
      [
        'openid',
        'st.account_service.all', // needed for calling account-service
        'email', // needed for calling monolith web apis
        'st.multi_tenant_user_apis', // needed for calling monolith web apis
        'eh.ids', // needed for making impersonation data available in the user token
        'stcrm.api', // needed for forwarding user token to the Leads API
        'st.task_hub.workspace.read', // needed for Task Hub access
        'st.task_hub.board.read',
        'st.task_hub.externalentity.write',
        'st.task_hub.task.read',
        'st.task_hub.task.write',
        'st.task_hub.namespace.crm.read',
        'st.task_hub.namespace.crm.write',
      ],
      '/signin-oidc',
      '/signout-callback-oidc',
      this.app.logger,
      async (authenticated: boolean) => {
        if (authenticated) {
          this.setAuthenticated();
        }
      },
    );

    // create the UserSubscriptionManager
    this.subscriptionManager = new UserSubscriptionManager(
      this.app.logger,
      this.app.events,
    );
  }

  override preload(): void {
    // Wrap event handlers in try-catch for defensive programming.
    // Even though internal methods have error handling, this provides
    // an extra safety layer to prevent unhandled promise rejections
    // that could propagate to the event system and cause app instability.
    this.app.events.on('app.started', async () => {
      try {
        // Await path selection before branching — this lets the kill switch clear
        // a stale 'session' sticky before we decide whether to probe the BFF.
        // Falls back gracefully (no-op) on LD error so auth still attempts to
        // restore via PKCE if a session sticky was never written.
        const authPath = await selectAuthPath().catch((err: unknown) => {
          this.app.logger.warn('Auth path LD eval failed', err);
          return readStickyPath(); // fall back to whatever is in localStorage
        });

        if (authPath === 'session') {
          // BFF session path: probe /bff/authinfo to determine if already signed in.
          // authFlow.signedIn() will be false because no PKCE token exists.
          const bffUser = await probeBffUser();
          if (bffUser !== null) {
            await this.setAuthenticated();
          }
        } else if (this.authFlow.signedIn()) {
          await this.setAuthenticated();
        }
      } catch (err) {
        // Log unexpected errors that escape internal error handling
        this.app.logger.error('Error in app.started handler', err);
      }
    });

    this.app.events.on('app.wake', async () => {
      try {
        // Check our auth session duration
        await this.checkAuthAge();

        // Handle subscription and polling via manager
        await this.subscriptionManager.handleWake();
      } catch (err) {
        // Log unexpected errors that escape internal error handling
        this.app.logger.error('Error in app.wake handler', err);
      }
    });
  }

  /**
   * Initiates the sign-in process for the user.
   *
   * @param idp - Optional identity provider to use for sign-in.
   * @returns A promise that resolves when the sign-in process is complete.
   *
   * @remarks
   * - If the user is already signed in, it checks the authentication age and
   *   sets the state as authenticated.
   * - If the user is not signed in, it requests authorization from the
   *   specified identity provider.
   */
  public async signin(idp?: string): Promise<void> {
    authState.loading = true;
    authState.loadingState = 'signin';
    authState.error = undefined;

    // if we're already signed in, make sure we're not stale
    // and set our state as authenticated
    if (this.authFlow.signedIn()) {
      await this.checkAuthAge();
      this.setAuthenticated();
      return;
    }

    // request authorization
    this.authFlow.requestAuthorization(undefined, idp);
  }

  /**
   * Initiates sign-in using the BFF session path.
   *
   * Performs a top-level navigation to `/bff/login?returnUrl=<current-path>`
   * so the server-side BFF handles the OpenID Connect flow. No PKCE
   * authorization or token redemption happens in the browser.
   *
   * @param returnUrl - The URL to return to after the BFF completes sign-in.
   *   Defaults to the current page path + search + hash.
   */
  public signinViaBff(returnUrl?: string): void {
    // Delegate to the shared helper so the /bff/login URL is built once and
    // targets the BFF (API_URL) origin, consistent with the /bff/user probe.
    redirectToBffLogin(returnUrl);
  }

  /**
   * Completes the sign-in process by finalizing the authorization flow.
   * If the authorization is successful, it resets our auth age tracker.
   *
   * Uses defensive error handling to prevent OAuth callback failures from
   * causing unhandled rejections. Logs errors, sets auth state, but doesn't throw.
   *
   * @returns {Promise<void>} A promise that resolves when the sign-in process
   * is complete (even if it fails).
   */
  public async completeSignin(): Promise<void> {
    const startTime = Date.now();

    try {
      const success = await this.authFlow.completeAuthorization();

      if (success) {
        this.resetAuthAge();
        this.app.logger.info('OAuth callback completed', {
          duration: Date.now() - startTime,
        });
      } else {
        this.app.logger.warn('OAuth callback failed', {
          duration: Date.now() - startTime,
          reason: 'authorization_not_successful',
        });
      }
    } catch (err) {
      // Defensive error handling: OAuth callback errors should not crash the app
      // Log error, set state, but don't throw (consistent with setAuthenticated pattern)
      const error = err instanceof Error ? err : new Error(String(err));

      this.app.logger.error('OAuth callback failed', error, {
        duration: Date.now() - startTime,
        errorMessage: error.message,
        errorName: error.name,
      });

      // Set error state so UI can show appropriate message
      authState.error = error.message;
      authState.loading = false;
      authState.loadingState = 'none';

      // Report to diagnostics for tracking
      DiagnosticsModule.reportBackgroundError(
        error,
        'OAuth callback failed during completeSignin',
        {
          priority: 'passive',
          source: 'auth-complete-signin',
          extra: {
            duration: Date.now() - startTime,
          },
        },
      );

      // Don't throw - fail gracefully (defensive programming)
    }
  }

  /**
   * Gets the signed-in status of the user.
   *
   * @returns {boolean} - Returns `true` if the user is signed in,
   * otherwise `false`.
   */
  public get signedIn(): boolean {
    return authState.signedIn;
  }

  /**
   * Signs the user out by executing any registered signout handlers and
   * updating the authentication state.
   *
   * @returns {Promise<void>} A promise that resolves when the signout process
   * is complete.
   */
  public async signout(): Promise<void> {
    if (authState.loading && authState.loadingState === 'signout') {
      return;
    }

    // set our state for signing out
    authState.loading = true;
    authState.loadingState = 'signout';
    authState.error = undefined;

    // see if we have any handlers that prevent signout
    if (!(await this.canSignOut())) {
      authState.loading = false;
      authState.loadingState = 'none';
      return;
    }

    // run signout handlers
    const handlerStartTime = Date.now();

    this.app.logger.info('Signout handlers started', {
      handlerCount: Object.keys(this.signoutHandlers).length,
      handlers: Object.keys(this.signoutHandlers),
    });

    // Execute all handlers even if some fail - wrap each in try-catch
    // to ensure cleanup handlers after a failed one still execute and
    // setNotAuthenticated() always runs to complete local signout
    for (const [key, handler] of Object.entries(this.signoutHandlers)) {
      try {
        await handler();
      } catch (err) {
        // Log error but continue with remaining handlers
        // This is best-effort cleanup - individual handler failures
        // should not block the entire signout process
        this.app.logger.error(`Signout handler '${key}' failed`, err);
      }
    }

    this.app.logger.info('Signout handlers completed', {
      duration: Date.now() - handlerStartTime,
    });

    this.setNotAuthenticated();

    // Session path: log out through the BFF so it clears the server-side session
    // (cookie + token store) and drives the upstream OIDC end-session. Hitting the
    // IdP's /connect/endsession directly (the PKCE path below) would leave the BFF
    // cookie intact. This navigates away, so nothing after it runs.
    if (readStickyPath() === 'session') {
      try {
        await redirectToBffLogout();
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to complete BFF logout';
        this.app.logger.error('Signout failed at BFF logout', {
          error: errorMessage,
        });
        authState.loading = false;
        authState.loadingState = 'none';
        authState.error = errorMessage;
        this.app.events.emit('auth.signout.failed', {
          error: errorMessage,
          locallySignedOut: true,
        });
      }
      return;
    }

    // PKCE path: request end session at the IdP with error handling.
    try {
      this.authFlow.requestEndSession();
      // If redirect succeeds, the page will navigate away
      // and the code below won't execute
    } catch (error: any) {
      // If end session request fails, log the error and update state
      const errorMessage =
        error?.message ?? 'Failed to complete logout with identity provider';

      this.app.logger.error('Signout failed at end session request', {
        error: errorMessage,
        originalError: error,
      });

      // Update state to reflect the error
      authState.loading = false;
      authState.loadingState = 'none';
      authState.error = errorMessage;

      // Even though IdP logout failed, we've already cleared local state
      // and run signout handlers, so the user is effectively logged out locally.
      // We could optionally emit an event here to notify other modules
      this.app.events.emit('auth.signout.failed', {
        error: errorMessage,
        locallySignedOut: true,
      });
    }
  }

  /**
   * Registers a handler that determines if the user can sign out.
   *
   * @param key - A unique identifier for the handler.
   * @param handler - The handler function that will be called to check if the
   * user can sign out.
   */
  public registerCanSignoutHandler(
    key: string,
    handler: AuthCanSignoutHandler,
  ): void {
    this.canSignoutHandlers[key.toLowerCase()] = handler;
  }

  /**
   * Registers a signout handler for a given key.
   *
   * @param key - The key associated with the signout handler.
   * @param handler - The handler function to be called on signout.
   * @returns void
   */
  public registerSignoutHandler(
    key: string,
    handler: AuthSignoutHandler,
  ): void {
    this.signoutHandlers[key.toLowerCase()] = handler;
  }

  /**
   * Unregisters a handler for the sign-out event.
   *
   * @param key - The key associated with the handler to be removed. The key is
   * case-insensitive.
   */
  public unregisterCanSignoutHandler(key: string): void {
    delete this.canSignoutHandlers[key.toLowerCase()];
  }

  /**
   * Unregisters a signout handler by its key.
   *
   * @param key - The key of the signout handler to unregister.
   */
  public unregisterSignoutHandler(key: string): void {
    delete this.signoutHandlers[key.toLowerCase()];
  }

  /**
   * Gets the current authenticated user.
   *
   * @returns {AuthUser | undefined} The authenticated user if available,
   * otherwise undefined.
   */
  public get user(): AuthUser | undefined {
    return authState.user;
  }

  /**
   * Checks if the user has access to a specific tenant.
   */
  public hasAccessToTenant(tenantId: string): boolean {
    return authState.user?.org.tenants.some((t) => t.id === tenantId) ?? false;
  }

  /**
   * Retrieves the current authentication token.
   *
   * This method checks the age of the current authentication session and
   * refreshes the tokens if necessary. If the token is invalid or close to
   * expiration, it will refresh the tokens and update the authentication
   * state with the new access token.
   *
   * Uses promise deduplication to ensure only one token refresh happens
   * at a time, even when multiple API requests are made simultaneously.
   *
   * If token refresh fails but the current token is still valid, falls back
   * to the current token to maintain service availability during temporary
   * token server outages.
   *
   * @returns {Promise<string>} A promise that resolves to the current access
   * token.
   */
  public async getCurrentToken(): Promise<string> {
    // Use the synchronous sticky-path read — avoids the async LD evaluation
    // (5-second timeout) on every token request, which breaks tests.
    const authPath = readStickyPath();

    if (authPath === 'session') {
      // BFF session path: fetch the bridge token on-demand from the BFF token
      // endpoint. The token is held in-memory only — NEVER written to any storage.
      // On refresh failure the error propagates so callers can handle it (e.g.
      // Apollo error link redirects to /bff/login on 401).
      // Dedup guard: concurrent callers share the same in-flight fetch rather
      // than each issuing their own request to the BFF token endpoint.
      this.bffTokenFetchPromise ??= fetchBffToken().finally(() => {
        this.bffTokenFetchPromise = null;
      });
      const token = await this.bffTokenFetchPromise;
      // Keep authState.token updated so useAuth().token consumers stay consistent.
      authState.token = token;
      return token;
    }

    // PKCE path: unchanged behavior below.

    // Early check: if a refresh is already in progress, wait for it
    // This must be checked BEFORE any awaits to prevent race conditions
    if (await this.waitForExistingTokenRefresh()) {
      return this.authFlow.accessToken!;
    }

    // check our auth session duration
    await this.checkAuthAge();

    // refresh tokens if needed
    if (!this.authFlow.isTokenValid(TOKEN_REFRESH_BUFFER_SECONDS)) {
      // Store current token state before attempting refresh
      const currentToken = this.authFlow.accessToken;
      const isCurrentTokenStillValid = this.authFlow.isTokenValid(0);

      // Prevent race condition when multiple services request tokens simultaneously
      this.tokenRefreshPromise ??= this.refreshTokensWithRetry()
        .then((token) => {
          authState.token = token;
          return token;
        })
        .finally(() => {
          this.tokenRefreshPromise = null;
        });

      try {
        // Wait for the refresh (either the one we just started or an existing one)
        await this.tokenRefreshPromise;
      } catch (error) {
        // warn — downstream of upstream error log; avoids double-counting in Sentry
        this.app.logger.warn('Token refresh failed', error);

        // If current token is still valid, use it as fallback
        // This maintains service availability during temporary token server outages
        if (isCurrentTokenStillValid && currentToken) {
          this.app.logger.warn(
            'Using current valid token as fallback after refresh failure',
          );
          return currentToken;
        }

        // warn — downstream of upstream error log; avoids double-counting in Sentry
        this.app.logger.warn(
          'Cannot use fallback token - token is expired or missing',
        );
        throw error;
      }
    }

    return this.authFlow.accessToken!;
  }

  /**
   * Retrieves the current service ID from the authentication state.
   *
   * @returns {string} The current service ID if available, otherwise an empty
   * string.
   */
  public getCurrentServiceId(): string {
    return authState.serviceId || '';
  }

  /**
   * Resets the authentication age by updating the `refreshedAt` property
   * of the `authState` object to the current date and time.
   */
  public resetAuthAge(): void {
    authState.refreshedAt = Date.now();
    this.app.logger.info('Auth age reset');
  }

  /**
   * Retrieves a new web phone token for the user.
   *
   * This method fetches a new web phone token and updates the user's
   * `webPhoneToken` property in the authentication state.
   *
   * @returns {Promise<string>} A promise that resolves to the new web phone
   * token.
   */
  public async getNewWebPhoneToken(): Promise<string> {
    const user = authState.user;
    if (!user || !hasValidSeat(user.seatLevel)) {
      const error = new Error(
        'Cannot fetch web phone token without a valid seat',
      );
      this.app.logger.warn(error.message, {
        seatLevel: user?.seatLevel,
      });
      throw error;
    }

    try {
      const token = await getWebPhoneToken();
      user.webPhoneToken = token;
      this.app.events.emit(AuthEvents.USER_UPDATED, user);
      return token;
    } catch (err) {
      this.app.logger.error('Error getting new web phone token', err);
      throw new Error(extractErrorMessage(err));
    }
  }

  /**
   * Checks if the user can sign out by invoking all registered sign-out
   * handlers.
   *
   * This method iterates through all handlers in `canSignoutHandlers` and
   * executes them. If any handler returns `false`, the method returns `false`,
   * indicating that the user cannot sign out. If all handlers return `true`,
   * the method returns `true`, indicating that the user can sign out.
   *
   * Handlers are wrapped in try-catch for defensive programming:
   * - External module code may have bugs that cause exceptions
   * - Exceptions are treated as "cannot sign out" (safer default)
   * - Prevents state corruption and unhandled promise rejections
   * - Clear logging helps identify broken handlers for debugging
   *
   * @returns {Promise<boolean>} A promise that resolves to `true` if the user
   *                             can sign out, or `false` if any handler
   *                             prevents the sign-out or throws an exception.
   */
  private async canSignOut(): Promise<boolean> {
    const canSignoutHandlers = Object.entries(this.canSignoutHandlers);
    for (const [key, handler] of canSignoutHandlers) {
      try {
        if (!(await handler())) {
          return false;
        }
      } catch (err) {
        // Treat handler exceptions as "cannot sign out" for safety.
        // If a handler fails to execute properly, we should err on the side
        // of caution and not allow signout to proceed.
        this.app.logger.error(
          `canSignout handler '${key}' threw an exception`,
          err,
        );
        return false;
      }
    }

    return true;
  }

  /**
   * Sets the authentication state for the application.
   *
   * If an error occurs during the process, it:
   * - Logs the error.
   * - Resets the authentication.
   *
   * @returns {Promise<void>} A promise that resolves when the authentication
   * state is set.
   * @throws {Error} Throws an error if no services are found or if the
   * user does not have an active contract.
   */
  private async setAuthenticated(): Promise<void> {
    if (authState.signedIn) {
      return;
    }

    authState.loading = true;
    authState.loadingState = 'signin';
    try {
      const authPath = readStickyPath();
      if (authPath === 'session') {
        // BFF session path: signed-in state is derived from the /bff/user probe.
        // We do NOT read any token from sessionStorage or authFlow.accessToken.
        // The token bridge (getCurrentToken → fetchBffToken) is set up separately
        // (task 5) and the Apollo authLink will call it for subsequent GQL calls.
        authState.token = '';
      } else {
        // PKCE path: use the PKCE access token as today.
        authState.token = this.authFlow.accessToken!;
      }

      if (!authState.serviceId) {
        // select default service
        const services = await getCurrentServices();
        if (services.length === 0) {
          throw new Error('No services found');
        }
        authState.serviceId = services[0].id;
        authState.serviceName = services[0].name;
      }
      authState.user = await getCurrentUser();
      if (!authState.user.org.hasActiveContract) {
        throw new Error(NO_ACTIVE_CONTRACT);
      }
      authState.signedIn = true;
      this.app.events.emit(AuthEvents.SIGNIN, this.user);
      // Setup GraphQL subscription for real-time user updates (fire-and-forget)
      void this.subscriptionManager.setup(authState.user.id);
    } catch (err) {
      this.app.logger.error('Error setting auth', err);
      this.authFlow.clear();
      authState.error = extractErrorMessage(err);
      authState.serviceId = '';
      authState.serviceName = '';
      authState.token = '';
      authState.user = undefined;
      authState.signedIn = false;
      authState.refreshedAt = 0;
    } finally {
      authState.loading = false;
      authState.loadingState = 'none';
    }
  }

  /**
   * Sets the authentication state to "not authenticated".
   */
  private setNotAuthenticated(): void {
    if (!authState.signedIn) {
      return;
    }

    authState.serviceId = '';
    authState.serviceName = '';
    authState.token = '';
    authState.user = undefined;
    authState.signedIn = false;
    authState.refreshedAt = 0;

    // Clean up subscription and polling
    this.subscriptionManager.cleanup();

    // Clear refresh interval
    if (this.refreshAuthInterval) {
      clearInterval(this.refreshAuthInterval);
      this.refreshAuthInterval = null;
    }

    this.app.events.emit(AuthEvents.SIGNOUT);
  }

  /**
   * Checks the authentication age and signs out the user if the age is
   * greater than the AUTH_FORCE_LOGIN_HOURS constant.
   */
  private async checkAuthAge(): Promise<void> {
    // Check our auth session duration.
    // IUM access tokens expire in ~1 hour and are refreshed automatically using the refresh token.
    // However, the EH IDP session (which backs the authentication) expires after 10 hours.
    // Once the EH session expires, IUM can no longer issue new tokens via refresh,
    // so we must sign out and require the user to re-authenticate.
    if (authState.user && this.getAuthAgeInHours > AUTH_FORCE_LOGIN_HOURS) {
      // make sure we can sign out
      if (!(await this.canSignOut())) {
        // Only show the error message once to avoid spamming the user
        // when the app wakes up repeatedly while they complete their current activity
        if (!this.sessionExpiredShown) {
          this.sessionExpiredShown = true;
          DiagnosticsModule.reportUserError(
            'Your session has expired and you need to sign in again, but something is preventing you from signing out. Please sign out manually and sign in again.',
            {
              priority: 'passive',
            },
          );
        }
        return;
      }

      // Reset the flag when signout succeeds so it can be shown again
      // in future expired session scenarios
      this.sessionExpiredShown = false;

      // EH IDP session has expired - refresh tokens will no longer work
      this.app.logger.info('Auth is stale, signing out');
      this.signout();
    }
  }

  /**
   * Calculates the age of the authentication session in hours.
   *
   * @returns {number} The age of the authentication session in hours.
   */
  private get getAuthAgeInHours(): number {
    return (Date.now() - authState.refreshedAt) / 1000 / 60 / 60;
  }

  /**
   * Waits for an existing token refresh if one is in progress.
   *
   * @returns {Promise<boolean>} True if a refresh was in progress and we waited for it,
   * false if no refresh was in progress.
   */
  private async waitForExistingTokenRefresh(): Promise<boolean> {
    if (this.tokenRefreshPromise) {
      await this.tokenRefreshPromise;
      return true;
    }
    return false;
  }

  /**
   * Refreshes tokens with retry logic and exponential backoff.
   *
   * Attempts to refresh the access token using the refresh token, with automatic
   * retries for temporary failures. Uses exponential backoff: 1s, 2s, 4s delays
   * between attempts.
   *
   * @param maxRetries - Maximum number of retry attempts (default: 3)
   * @returns {Promise<string>} The new access token
   * @throws {Error} If all retry attempts fail
   */
  private async refreshTokensWithRetry(maxRetries = 3): Promise<string> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const startTime = Date.now();
      try {
        const token = await this.authFlow.refreshTokens();
        const duration = Date.now() - startTime;

        this.app.logger.info('Token refresh succeeded', {
          attempt,
          duration,
        });

        return token;
      } catch (error: any) {
        lastError = error as Error;
        const duration = Date.now() - startTime;
        const errorType = error?.errorType ?? 'unknown';

        this.app.logger.warn(
          `Token refresh attempt ${attempt}/${maxRetries} failed`,
          {
            attempt,
            maxRetries,
            error: extractErrorMessage(error),
            errorType,
            errorCode: error?.errorCode,
            duration,
          },
        );

        // Don't retry auth errors - they won't succeed on retry.
        // warn — downstream of upstream error log; avoids double-counting in Sentry
        if (errorType === 'auth') {
          this.app.logger.warn(
            'Token refresh failed with auth error - not retrying',
            {
              errorType,
              errorCode: error?.errorCode,
            },
          );
          throw error;
        }

        // Don't delay after the last attempt
        if (attempt < maxRetries) {
          const delayMs = Math.pow(2, attempt - 1) * 1000; // Exponential backoff: 1s, 2s, 4s
          this.app.logger.debug(
            `Retrying token refresh in ${delayMs}ms (error type: ${errorType})`,
          );
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
    }

    // All attempts failed
    this.app.logger.error('Token refresh failed after all retry attempts', {
      maxRetries,
      error: extractErrorMessage(lastError),
    });

    throw lastError;
  }
}

export default new AuthModule(appManager.events, appManager.logger('Auth'));
export type { AuthUser } from './types';
